import { createId } from "@paralleldrive/cuid2";
import { DotsSixVertical, Envelope, Plus, X } from "@phosphor-icons/react";
import type { CustomField as ICustomField } from "@reactive-resume/schema";
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { AnimatePresence, Reorder, useDragControls } from "framer-motion";

import { useCoverLetterStore } from "@/client/stores/cover-letter";

type CustomFieldProps = {
  field: ICustomField;
  onChange: (field: ICustomField) => void;
  onRemove: (id: string) => void;
};

const CustomField = ({ field, onChange, onRemove }: CustomFieldProps) => {
  const controls = useDragControls();

  const handleChange = (key: "icon" | "name" | "value", value: string) => {
    onChange({ ...field, [key]: value });
  };

  return (
    <Reorder.Item
      value={field}
      dragListener={false}
      dragControls={controls}
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
    >
      <div className="flex items-end justify-between">
        <Button
          size="icon"
          variant="ghost"
          className="shrink-0"
          onPointerDown={(event) => controls.start(event)}
        >
          <DotsSixVertical />
        </Button>

        <Popover>
          <Tooltip content="Icon">
            <PopoverTrigger asChild>
              <Button size="icon" variant="ghost" className="shrink-0">
                {field.icon ? <i className={cn(`ph ph-${field.icon}`)} /> : <Envelope />}
              </Button>
            </PopoverTrigger>
          </Tooltip>
          <PopoverContent side="bottom" align="start" className="flex flex-col gap-y-1.5 p-1.5">
            <Input
              value={field.icon}
              placeholder="Enter Phosphor Icon"
              onChange={(event) => onChange({ ...field, icon: event.target.value })}
            />
          </PopoverContent>
        </Popover>

        <Input
          className="mx-2"
          placeholder="Name"
          value={field.name}
          onChange={(event) => handleChange("name", event.target.value)}
        />

        <Input
          className="mx-2"
          placeholder="Value"
          value={field.value}
          onChange={(event) => handleChange("value", event.target.value)}
        />

        <Button size="icon" variant="ghost" className="shrink-0" onClick={() => onRemove(field.id)}>
          <X />
        </Button>
      </div>
    </Reorder.Item>
  );
};

type Props = { className?: string };

export const CLCustomFieldsSection = ({ className }: Props) => {
  const setValue = useCoverLetterStore((state) => state.setValue);
  const customFields = useCoverLetterStore(
    (state) => state.coverLetter.data.sender.customFields,
  );

  const handleAdd = () => {
    setValue("sender.customFields", [
      ...customFields,
      { id: createId(), icon: "envelope", name: "", value: "" },
    ]);
  };

  const handleChange = (field: ICustomField) => {
    const index = customFields.findIndex((item) => item.id === field.id);
    const updated = JSON.parse(JSON.stringify(customFields));
    updated[index] = field;
    setValue("sender.customFields", updated);
  };

  const handleReorder = (values: ICustomField[]) => {
    setValue("sender.customFields", values);
  };

  const handleRemove = (id: string) => {
    setValue(
      "sender.customFields",
      customFields.filter((field) => field.id !== id),
    );
  };

  return (
    <div className={cn("space-y-4", className)}>
      <AnimatePresence>
        <Reorder.Group
          axis="y"
          className="space-y-4"
          values={customFields}
          onReorder={handleReorder}
        >
          {customFields.map((field) => (
            <CustomField
              key={field.id}
              field={field}
              onChange={handleChange}
              onRemove={handleRemove}
            />
          ))}
        </Reorder.Group>
      </AnimatePresence>

      <Button variant="link" onClick={handleAdd}>
        <Plus className="mr-2" />
        <span>Add a custom field</span>
      </Button>
    </div>
  );
};
